import React, { useEffect, useMemo, useState } from "react";

import { useRouter } from "next/router";
import Link from "next/link";
import { useAppState } from "lib/appState";
import { googleLoginApi, loginApi } from "api/auth.api";
import Head from "next/head";
import Loader from "components/atoms/Loader";
import { Alert, Typography } from "@mui/material";

import Button from "components/Button/Button";
import { Box } from "@mui/system";
import ReworkedLayout from "layouts/MarvyLayout";
import MarvyTextField from "components/atoms/MarvyTextField";
import Row from "components/atoms/Row";
import LoginWithGoogleButton from "components/LoginWithGoogleButton";
import { MT } from "components/MarvyTypography";
import { saveLoginSession } from "lib/auth";
import { errorName, getErrorMessage } from "lib/errors";
import features from "config/features";
import { emitGTMEvent } from "lib/events";

function Field({ ...props }) {
  return <MarvyTextField sx={{ mb: "30px" }} {...props} />;
}

export default function Register() {
  const router = useRouter();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [data, setData] = useState({
    email: "",
    password: "",
  });
  const [appState, setAppState] = useAppState();

  const { verification } = router.query;

  const [verificationHandled, setVerificationHandled] = useState(false);

  const emitVerificationEvent = useMemo(
    () => async () => {
      if (appState.registeredUser) {
        await emitGTMEvent({
          event: "track",
          eventName: "verifiedSignUp",
          userId: appState.registeredUser.id,
        });
      }
    },
    [appState]
  );

  useEffect(() => {
    if (verification && !verificationHandled) {
      switch (verification) {
        case "successful":
          setMessage(
            "You've successfully verified your email address. You may now log in."
          );
          emitVerificationEvent();
          break;
        case "alreadyVerified":
          setError("__already_verified");
          break;
        default:
          setError("__couldnt_verify");
          break;
      }
      setVerificationHandled(true);
    }
  }, [verification, verificationHandled, emitVerificationEvent]);

  useEffect(() => {
    if (appState.tokens) {
      router.push("/dashboard");
    }
  }, [router, appState.tokens]);

  async function login({ email, password }) {
    try {
      setLoading(true);
      let res = await loginApi({ email, password });
      await saveLoginSession({
        user: res.data.user,
        tokens: res.data.tokens,
        appState,
        setAppState,
        router,
      });
    } catch (e) {
      setLoading(false);
      setError(errorName(e));
    }
  }

  function submit(e) {
    e.preventDefault();
    login(data);
  }

  console.log({ error });

  async function onGoogleLogin(data) {
    setLoading(true);
    await setAppState({ googleLoginCredential: data.credential });
    try {
      const res = await googleLoginApi({
        credential: data.credential,
      });

      await saveLoginSession({
        user: res.data.user,
        tokens: res.data.tokens,
        appState,
        setAppState,
        router,
      });
    } catch (e) {
      if (e.response?.data.name === "user_doesnt_exist") {
        await router.push("/auth/register/google");
      } else {
        setError(errorName(e));
      }
    }
    setLoading(false);
  }

  return (
    <ReworkedLayout noMenu unauthenticated>
      <Head>
        <title>Log in to Uptimeline</title>
        <meta
          name="description"
          content="With Uptimeline you get up to 20 uptime monitors for free, forever!"
        />
      </Head>
      {features.googleSingIn ? (
        <>
          <Row sx={{ justifyContent: "center" }}>
            <LoginWithGoogleButton onLogin={onGoogleLogin} />
          </Row>
          <MT sx={{ alignSelf: "center", my: 4 }}>OR</MT>
        </>
      ) : null}
      <Box
        sx={{
          paddingBottom: "30px",
        }}
      >
        <Typography
          // variant="h1"
          sx={{
            fontWeight: "700",
            fontSize: "24px",
            color: "white.main",
          }}
          align="center"
        >
          Log in with your email
        </Typography>
      </Box>
      {loading ? <Loader /> : null}
      <Box
        component="form"
        onSubmit={submit}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          alignItems: "stretch",
          px: "12px",
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ marginBottom: "40px" }}>
            {" "}
            {getErrorMessage(error)}
          </Alert>
        ) : null}
        {message ? (
          <Alert severity="success" sx={{ marginBottom: "40px" }}>
            {" "}
            {message}
          </Alert>
        ) : null}
        <Field
          type="email"
          label="Email"
          name="email"
          placeholder="john.doe@company.com"
          value={data.email}
          required
          onChange={(e) => setData({ ...data, email: e.target.value })}
        />
        <Field
          type="password"
          label="Password"
          name="password"
          placeholder="*******"
          value={data.password}
          required
          onChange={(e) => setData({ ...data, password: e.target.value })}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Button type="submit" gradient>
            Log in
          </Button>
          <Box sx={{ mb: "30px" }} />
          <Link href="/auth/register">
            <Button variant="text">Sign up for a new account instead</Button>
          </Link>
          <Link href="/auth/forgotPassword">
            <Button variant="text">I forgot my password</Button>
          </Link>
        </Box>
      </Box>
    </ReworkedLayout>
  );
}
